import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Paper, Divider, Chip, CircularProgress } from '@mui/material';
import ComputerOutlinedIcon from '@mui/icons-material/ComputerOutlined';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import { DataProviderInstance } from '../../../api/DataProvider';
import CustomButton from '../../../components/buttons/CustomButton';
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog';
import userAgentLabel, { isMobileUserAgent } from '../../../utils/userAgentLabel';
import { formatDate } from '../../../utils/Utils';

// "2026-09-30 10:00:00" (horário do servidor) -> "30/09/2026 10:00"
const formatSessionDate = (value) => {
  if (!value) return '';
  const date = new Date(String(value).replace(' ', 'T'));
  return isNaN(date.getTime()) ? String(value) : formatDate(date);
};

const errorMessageOf = (response, fallback) => (response && (response.error || response.message)) || fallback;

/**
 * Sessões ativas do usuário logado (L-21): onde a conta está aberta, com a opção de
 * encerrar uma sessão específica ou todas as outras. A sessão atual não pode ser
 * encerrada aqui (para isso existe o "Sair").
 *
 * Props:
 *   onFeedback(message, severity) - mostra o aviso no snackbar da página
 */
const ActiveSessions = ({ onFeedback }) => {
  const { ListUserSessions, RevokeUserSession } = DataProviderInstance();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  // { session } para encerrar uma sessão, { others: true } para encerrar todas as outras
  const [pendingRevoke, setPendingRevoke] = useState(null);
  const [revoking, setRevoking] = useState(false);

  const loadSessions = useCallback(() => {
    setLoading(true);
    ListUserSessions((response) => {
      if (response && response.status === 'SUCCESS' && response.data) {
        setSessions(response.data.sessions || []);
        setLoadError('');
      } else {
        setLoadError(errorMessageOf(response, 'Não foi possível carregar as sessões.'));
      }
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const otherSessionsCount = sessions.filter((session) => !session.current).length;

  const closeConfirm = () => {
    if (!revoking) setPendingRevoke(null);
  };

  const confirmRevoke = () => {
    if (!pendingRevoke) return;
    const target = pendingRevoke.others ? { others: true } : { sessionId: pendingRevoke.session.id };

    setRevoking(true);
    RevokeUserSession(target, (response) => {
      setRevoking(false);
      setPendingRevoke(null);

      if (response && response.status === 'SUCCESS') {
        const revoked = (response.data && response.data.revoked) || 0;
        const message = target.others
          ? (revoked === 1 ? '1 sessão encerrada.' : `${revoked} sessões encerradas.`)
          : 'Sessão encerrada.';
        onFeedback && onFeedback(message, 'success');
      } else {
        onFeedback && onFeedback(errorMessageOf(response, 'Não foi possível encerrar a sessão.'), 'error');
      }
      loadSessions();
    });
  };

  const renderSession = (session) => {
    const DeviceIcon = isMobileUserAgent(session.userAgent) ? SmartphoneOutlinedIcon : ComputerOutlinedIcon;

    return (
      <Box
        key={session.id}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          padding: '14px 16px',
          borderRadius: '10px',
          border: '1px solid #E2E8F0',
          backgroundColor: session.current ? 'rgba(25, 118, 210, 0.04)' : '#FFFFFF',
          flexWrap: { xs: 'wrap', sm: 'nowrap' }
        }}
      >
        <DeviceIcon sx={{ color: '#64748B', flexShrink: 0 }} />

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body1" fontWeight={600}>
              {userAgentLabel(session.userAgent)}
            </Typography>
            {session.current && (
              <Chip
                label="Atual"
                color="primary"
                variant="outlined"
                size="small"
                sx={{
                  fontWeight: 'bold',
                  borderRadius: '16px',
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  borderColor: 'rgba(25, 118, 210, 0.5)'
                }}
              />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {session.ip ? `IP ${session.ip}` : 'IP não informado'}
            {' · '}Última atividade em {formatSessionDate(session.lastSeenAt)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Iniciada em {formatSessionDate(session.createdAt)}
          </Typography>
        </Box>

        {!session.current && (
          <CustomButton
            style="secondary"
            variant="outlined"
            size="small"
            label="Encerrar"
            onClick={() => setPendingRevoke({ session })}
            disabled={revoking}
            sx={{ flexShrink: 0 }}
          />
        )}
      </Box>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.03)',
        border: '1px solid #E2E8F0'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Sessões ativas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Navegadores e dispositivos em que sua conta está aberta.
          </Typography>
        </Box>
        <CustomButton
          style="secondary"
          variant="contained"
          icon={<LogoutIcon />}
          label="Encerrar todas as outras sessões"
          onClick={() => setPendingRevoke({ others: true })}
          disabled={loading || revoking || otherSessionsCount === 0}
        />
      </Box>
      <Divider sx={{ margin: '16px 0' }} />

      {loading && sessions.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
          <CircularProgress size={28} />
        </Box>
      ) : loadError ? (
        <Typography variant="body2" color="error">
          {loadError}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {sessions.map(renderSession)}
        </Box>
      )}

      <ConfirmDialog
        isOpen={!!pendingRevoke}
        variant="warning"
        title={pendingRevoke && pendingRevoke.others ? 'Encerrar as outras sessões?' : 'Encerrar esta sessão?'}
        message={pendingRevoke && pendingRevoke.others
          ? 'Sua conta será desconectada de todos os outros navegadores e dispositivos. Esta sessão continua aberta.'
          : pendingRevoke
            ? `A conta será desconectada de ${userAgentLabel(pendingRevoke.session.userAgent)}. Para voltar a usar ali, será preciso entrar de novo.`
            : ''}
        confirmLabel="Encerrar"
        onConfirm={confirmRevoke}
        onCancel={closeConfirm}
        loading={revoking}
      />
    </Paper>
  );
};

export default ActiveSessions;
